import { useState, useEffect } from "react";
import { X, CheckCircle2, Calendar } from "lucide-react";
import { fmtRub } from "@/shared/lib/format";
import { Avatar } from "./Avatar";
import { PlatformIcon } from "./PlatformIcon";
import { useCampaigns, useCampaignCreatives, type Blogger } from "@/shared/api/wb";
import { useDraftStore } from "@/shared/store/draft";
import type { CartSelection, PlatformKey } from "@/features/blogger-card";
import { PLATFORM_LABELS } from "@/features/blogger-card";

const AVATAR_GRADS: Record<string, [string, string]> = {
  telegram: ["#9FE1CB", "#085041"], tg: ["#9FE1CB", "#085041"],
  vk:       ["#B5D4F4", "#0C447C"],
  youtube:  ["#F5C4B3", "#712B13"], yt: ["#F5C4B3", "#712B13"],
  dzen:     ["#E8E8E8", "#444"],
  tiktok:   ["#E0E0E5", "#1A1D22"], tt: ["#E0E0E5", "#1A1D22"],
};

const STATUS_RU: Record<string, string> = {
  active: "активна", moderation: "на модерации", draft: "черновик", done: "завершена",
};

type Props = {
  blogger: Blogger;
  cartSelection: CartSelection;
  /** campaignId is "draft" for the new-campaign draft, or an existing
   * campaign's id — the caller decides how to add the blogger based on it.
   * date is only collected (and only required) for an existing campaign —
   * a draft still goes through the wizard's own date step later. */
  onConfirm: (variantId: string | null, campaignId: string, date?: string) => void;
  onClose: () => void;
  preselectedCampaignId?: string;
};

export function CartPickerModal({ blogger, cartSelection, onConfirm, onClose, preselectedCampaignId }: Props) {
  const { data: campaigns = [] } = useCampaigns();
  const draftVariants = useDraftStore(s => s.draft.variants);

  const openCampaigns = campaigns.filter(c => c.status !== "done");

  const [selectedCampaign, setSelectedCampaign] = useState<string>(
    preselectedCampaignId ?? "draft",
  );

  const isExistingCampaign = selectedCampaign !== "draft";
  const { data: campaignCreativesData } = useCampaignCreatives(isExistingCampaign ? selectedCampaign : null);

  const variants = isExistingCampaign
    ? (campaignCreativesData?.variants ?? [])
    : draftVariants;

  const [selectedVariant, setSelectedVariant] = useState<string>(
    variants.length > 0 ? variants[0].id : "__later__",
  );
  const [date, setDate] = useState("");

  useEffect(() => {
    setSelectedVariant(variants.length > 0 ? variants[0].id : "__later__");
  }, [selectedCampaign]); // eslint-disable-line react-hooks/exhaustive-deps

  const dateMissing = isExistingCampaign && !date;

  const grad = AVATAR_GRADS[blogger.platform] ?? ["#BFF93F", "#1C1C1C"];
  const platformKey = cartSelection.platform as PlatformKey;
  const isPosev = cartSelection.type === "posev";

  const selectCls =
    "w-full h-10 px-3 rounded-lg border border-ink-12 bg-white text-[13.5px] font-display font-semibold outline-none focus:border-ink appearance-none cursor-pointer";

  return (
    <div
      className="fixed inset-0 z-[120] grid place-items-center"
      style={{ background: "rgba(28,28,28,0.55)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        className="bg-white rounded-xl shadow-2xl fade-up flex flex-col"
        style={{ width: "min(440px, 96vw)" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-ink-12">
          <span className="font-display font-extrabold text-[17px] tracking-tight">Добавить в РК</span>
          <button onClick={onClose} className="text-ink-40 hover:text-ink transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Blogger summary */}
        <div className="mx-5 mt-4 flex items-center gap-3 p-3 rounded-lg"
          style={{ background: isPosev ? "#EAF3DE" : "#E6F1FB" }}>
          <Avatar name={blogger.full} size={36} grad={grad} />
          <div className="flex-1 min-w-0">
            <div className="font-display font-semibold text-[13.5px] truncate">{blogger.name}</div>
            <div className="text-[11.5px] text-ink-60 flex items-center gap-1.5 flex-wrap">
              <PlatformIcon platform={blogger.platform as never} size={11} />
              {PLATFORM_LABELS[platformKey] ?? cartSelection.platform}
              <span className="text-ink-40">·</span>
              {isPosev ? "Посев" : "Натив"}
              <span className="text-ink-40">·</span>
              {cartSelection.formatName}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="font-display font-bold text-[15px]">{fmtRub(cartSelection.price)}</div>
            <div className="text-[10.5px] text-ink-40">CPV {cartSelection.cpv.toFixed(2)} ₽</div>
          </div>
        </div>

        {/* Fields */}
        <div className="px-5 pt-5 pb-2 flex flex-col gap-4">

          {/* Campaign select */}
          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] font-display font-bold uppercase tracking-[0.08em] text-ink-40">
              Кампания
            </span>
            <div className="relative">
              <select
                value={selectedCampaign}
                onChange={e => setSelectedCampaign(e.target.value)}
                className={selectCls}
              >
                <option value="draft">Новая РК (черновик)</option>
                {openCampaigns.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.product} — {STATUS_RU[c.status] ?? c.status}
                  </option>
                ))}
              </select>
              <Chevron />
            </div>
          </label>

          {/* Creative select */}
          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] font-display font-bold uppercase tracking-[0.08em] text-ink-40">
              Креатив
            </span>
            <div className="relative">
              <select
                value={selectedVariant}
                onChange={e => setSelectedVariant(e.target.value)}
                className={selectCls}
                disabled={variants.length === 0}
              >
                {variants.length === 0 ? (
                  <option value="__later__">Нет вариантов — назначить позже</option>
                ) : (
                  <>
                    {variants.map(v => {
                      const isNative = v.id.startsWith("nb");
                      const suffix = isNative ? " (ТЗ)" : v.text ? ` — ${v.text.slice(0, 40)}…` : "";
                      return (
                        <option key={v.id} value={v.id}>{v.name}{suffix}</option>
                      );
                    })}
                    <option value="__later__">Назначить позже</option>
                  </>
                )}
              </select>
              <Chevron />
            </div>
          </label>

          {/* Date — only for an existing campaign: it goes straight to the
              kanban with no later review step to catch a missing date, so
              it has to be required here (mirrors the in-campaign
              "Добавить каналы" panel). A new draft still collects it in
              the wizard, so skip it here to avoid asking twice. */}
          {isExistingCampaign && (
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] font-display font-bold uppercase tracking-[0.08em] text-ink-40 flex items-center gap-1.5">
                <Calendar size={11} className={dateMissing ? "text-warning" : undefined} /> Дата размещения
              </span>
              <input
                type="date"
                required
                value={date}
                onChange={e => setDate(e.target.value)}
                className={`h-10 px-3 rounded-lg border bg-white text-[13.5px] font-mono outline-none focus:border-ink ${dateMissing ? "border-warning" : "border-ink-12"}`}
              />
              {dateMissing && (
                <span className="text-[11px] text-warning font-display font-semibold">Без даты канал не добавится</span>
              )}
            </label>
          )}

        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-5 py-4 mt-1">
          <button onClick={onClose}
            className="h-10 px-5 rounded-pill border border-ink-12 text-[13px] font-display font-semibold text-ink-60 hover:border-ink/40 transition-colors">
            Отмена
          </button>
          <button
            onClick={() => onConfirm(selectedVariant === "__later__" ? null : selectedVariant, selectedCampaign, date || undefined)}
            disabled={dateMissing}
            className="h-10 px-6 rounded-pill bg-lime text-[#1A3A05] text-[13px] font-display font-bold inline-flex items-center gap-2 hover:bg-lime/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <CheckCircle2 size={15} /> Добавить в РК
          </button>
        </div>
      </div>
    </div>
  );
}

function Chevron() {
  return (
    <svg
      className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-ink-40"
      width={14} height={14} viewBox="0 0 14 14" fill="none"
    >
      <path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
